// All Redux selectors, dispatch calls, and router access for the Misc Work Queue live here.
// MiscellaneousMyWorkQueue consumes this hook and never calls useSelector/useDispatch/useHistory
// directly, which keeps the component free of infrastructure and its tests free of Redux mocks.
import { useEffect, useMemo } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { useLocation, useHistory } from 'react-router'
import {
  fetchMiscWorkQueueItems,
  setMiscWqFilters,
  resetMiscWqFilters,
} from './MiscellaneousMyWorkQueueActions'
import { setCartQuantity, setErrorToast } from '../../actions'
import * as api from '../../requests'

export const useMiscWorkQueue = ({ sublinks }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  const { items, template, loading, error, filters } = useSelector(
    (state) => state.miscWorkQueue || {},
  )

  const cartQuantity = useSelector((state) => state.app.cartQuantity, shallowEqual)

  const sourceTypeParam = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location])

  const selectedModuleId = useSelector(
    (state) => state.app.miscRecordsSelectedModules[sourceTypeParam],
  )

  const sublink = useMemo(
    () => (sublinks || []).find((x) => x.parameter === sourceTypeParam) || {},
    [sublinks, sourceTypeParam],
  )

  const modules = useMemo(
    () => (sublink.MiscRecordsModules || []).filter((m) => m.DisplayInMyWorkQueue),
    [sublink],
  )

  useEffect(() => {
    if (selectedModuleId) {
      dispatch(fetchMiscWorkQueueItems(selectedModuleId))
    }
  }, [selectedModuleId])

  // Intentionally does not clear downloadsItem — that is local UI state owned by the component.
  // Callers that need to also clear local state (e.g. the cartQuantity effect) do so themselves.
  const refreshQueue = () => {
    if (selectedModuleId) {
      dispatch(fetchMiscWorkQueueItems(selectedModuleId))
    }
  }

  const search = (term) => {
    dispatch(setMiscWqFilters({ search: term }))
    if (selectedModuleId) {
      dispatch(fetchMiscWorkQueueItems(selectedModuleId))
    }
  }

  const applyFilters = (newFilters, props) => {
    const submissionDate = (newFilters && newFilters.submissionDate) || {}
    dispatch(
      setMiscWqFilters({
        status: (newFilters && newFilters.status) || null,
        submissionStartDate: submissionDate.start || null,
        submissionEndDate: submissionDate.end || null,
      }),
    )
    if (selectedModuleId && (!props || props.getRecords !== false)) {
      dispatch(fetchMiscWorkQueueItems(selectedModuleId))
    }
  }

  const removeFilter = (name) => {
    if (name === 'search') {
      dispatch(setMiscWqFilters({ search: '' }))
    } else if (name === 'status') {
      dispatch(setMiscWqFilters({ status: null }))
    } else if (name === 'submissionDate') {
      dispatch(setMiscWqFilters({ submissionStartDate: null, submissionEndDate: null }))
    }
    if (selectedModuleId) {
      dispatch(fetchMiscWorkQueueItems(selectedModuleId))
    }
  }

  const resetFilters = () => {
    dispatch(resetMiscWqFilters())
    if (selectedModuleId) {
      dispatch(fetchMiscWorkQueueItems(selectedModuleId))
    }
  }

  const handleView = (row) => {
    history.push('/miscellaneous/view/' + row.FormDefinitionId + '/' + row.FilingNum)
  }

  const handleEditDraft = (row) => {
    history.push('/miscellaneous/draft/' + row.FilingId)
  }

  const handleCorrect = (row) => {
    history.push('/correct/miscellaneous/' + row.FormDefinitionId + '/' + row.FilingNum)
  }

  const navigate = (path) => {
    history.push(path)
  }

  // onDone lets callers run local state side effects (e.g. closing the confirm dialog)
  // after the API call completes, without the hook needing to know about that state.
  const deleteDraft = (filingId, onDone) => {
    api.DELETE_DRAFT_BY_ID(filingId, () => {
      refreshQueue()
      if (onDone) {
        onDone()
      }
    })
  }

  const deleteFiling = (filingId, onDone) => {
    api.DELETE_MISC_FILING_BY_ID(filingId, () => {
      refreshQueue()
      if (onDone) {
        onDone()
      }
    })
  }

  const updateCartQuantity = (receiptItemContainers) => {
    dispatch(setCartQuantity((receiptItemContainers || []).filter((x) => !x.IS_FREE).length))
  }

  const reportError = (msg) => {
    dispatch(setErrorToast(msg))
  }

  return {
    items,
    template,
    loading,
    error,
    filters,
    cartQuantity,
    selectedModuleId,
    sourceTypeParam,
    sublink,
    modules,
    search,
    applyFilters,
    removeFilter,
    resetFilters,
    refreshQueue,
    handleView,
    handleEditDraft,
    handleCorrect,
    navigate,
    deleteDraft,
    deleteFiling,
    updateCartQuantity,
    reportError,
  }
}
